import { useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { makeStoragePersisterEffectKey } from 'utils/recoilEffects';
import {
  stepBarPlacementState,
  StepBarPlacement,
  STEP_BAR_PLACEMENT_PERSISTER_VERSION,
} from '../state';

/**
 * Syncs the user's persisted step bar placement preference to the Recoil atom
 * on initial load. Runs once when `viewerSettings.stepBarPlacement` is available.
 *
 * The viewer's own drag handle (StepBar.tsx) only persists placement to this atom's
 * localStorage entry, not to the server-side viewerSettings mutation (that's only wired up
 * from the Account Settings page) -- so the server value can be stale relative to what's
 * already correctly restored from localStorage. Only apply it when no local preference
 * exists yet, otherwise it clobbers the user's in-viewer choice on every reload (VX-4618).
 */
export const useInitStepBarPlacement = (stepBarPlacement: Nullable<string>) => {
  const setStepBarPlacement = useSetRecoilState(stepBarPlacementState);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (stepBarPlacement && !hasInitialized.current) {
      hasInitialized.current = true;
      // Computed here (not hoisted to module scope) because stepBarPlacementState.key is read
      // eagerly at module-eval time otherwise, which broke on an unrelated import cycle through
      // ViewerContext.tsx (Navbar.test.tsx crashed with "Cannot read properties of undefined
      // (reading 'key')" since ../state hadn't finished initializing yet on that cycle).
      const storageKey = makeStoragePersisterEffectKey(
        stepBarPlacementState.key,
        STEP_BAR_PLACEMENT_PERSISTER_VERSION
      );
      if (window.localStorage.getItem(storageKey) != null) return;
      const placement = stepBarPlacement === 'top' ? StepBarPlacement.Top : StepBarPlacement.Bottom;
      setStepBarPlacement(placement);
    }
  }, [stepBarPlacement, setStepBarPlacement]);
};
